<!--
  MicrosoftCallback.svelte — Landing page for the Microsoft sign-in popup.

  Microsoft redirects the popup here after the user authorises. The page has
  no UI worth speaking of: it hands the authorization code back to the window
  that opened it and closes itself.

  It lives on our own origin so `postMessage` can be locked to that origin in
  both development (Vite on :5173) and production (server-served SPA), and so
  the opener's own URL — which carries the app-consent context — is never
  navigated away from.

  If the page is somehow reached outside a popup there is no opener to talk
  to, so it shows a short message rather than a blank screen.
-->
<script lang="ts">
	import { onMount } from 'svelte';
	import { t } from '../../i18n';

	/** Discriminator so the opener ignores unrelated postMessage traffic. */
	const MESSAGE_SOURCE = 'abstractid-microsoft';

	/** True when there is no opener to hand the code back to. */
	let orphaned = $state(false);

	onMount(() => {
		const params = new URLSearchParams(window.location.search);
		const opener = window.opener as Window | null;

		/** Nothing to report to — the user opened this URL directly. */
		if (!opener) {
			orphaned = true;
			return;
		}

		/* Forward whichever of code or error Microsoft returned, along with the
		   state so the opener can confirm it started this exchange. */
		opener.postMessage(
			{
				source: MESSAGE_SOURCE,
				code: params.get('code'),
				state: params.get('state'),
				error: params.get('error_description') || params.get('error')
			},
			window.location.origin
		);

		window.close();
	});
</script>

<div class="flex min-h-screen items-center justify-center p-6 text-center text-gray-500 dark:text-gray-400">
	{#if orphaned}
		<p>{$t("This page is part of the Microsoft sign-in flow and can be closed.")}</p>
	{:else}
		<p>{$t("Completing sign-in…")}</p>
	{/if}
</div>
